import { Info } from "lucide-react";
import React, { useMemo } from "react";
import { Alert } from "@mantine/core";
import DOMPurify from "dompurify";

import { useMessageTemplates } from "../../providers/notifications/StrapiCMSApiService";
import { MessageTypeTrigger } from "../../providers/notifications/types";

interface TemplateAttributes {
  enabled: boolean;
  trigger: MessageTypeTrigger;
  resource_type: string;
  template_id: string;
  message_content: string;
  Type: string | null;
}

interface Template {
  id: number;
  attributes: TemplateAttributes;
}

const allowedTriggers: readonly MessageTypeTrigger[] = [
  MessageTypeTrigger.PageLoad,
  MessageTypeTrigger.Custom,
];

const getAlertClassName = (type: string | null): string => {
  const baseClasses =
    "m-0 rounded-none border-l-0 border-r-0 border-t-0 text-sm font-semibold text-center p-3 sm:text-xs sm:p-2";

  switch (type) {
    case "Warning":
      return `${baseClasses} bg-yellow-50 border-yellow-300 text-yellow-800`;
    case "Error":
      return `${baseClasses} bg-red-50 border-red-300 text-red-800`;
    case "Info":
      return `${baseClasses} bg-blue-50 border-blue-300 text-blue-800`;
    case "Success":
      return `${baseClasses} bg-green-50 border-green-300 text-green-800`;
    default:
      return `${baseClasses} bg-yellow-50 border-yellow-300 text-yellow-800`;
  }
};

const DowntimeBanner = (): JSX.Element | null => {
  const { data: messageTemplates, isLoading } = useMessageTemplates();

  const bannerTemplate = useMemo(() => {
    if (!messageTemplates?.data) {
      return null;
    }
    return (messageTemplates.data as Template[]).find(
      (template) =>
        template.attributes.enabled &&
        allowedTriggers.includes(template.attributes.trigger) &&
        template.attributes.resource_type === "General" &&
        template.attributes.template_id === "downtime_banner"
    );
  }, [messageTemplates]);

  if (!bannerTemplate || isLoading) {
    return null;
  }

  return (
    <Alert
      icon={<Info size={16} />}
      variant="light"
      className={`${getAlertClassName(
        bannerTemplate.attributes.Type
      )} fixed top-0 left-0 right-0 z-50`}
      classNames={{
        body: "flex justify-center items-center",
        message: "text-center",
      }}
    >
      <span
        dangerouslySetInnerHTML={{
          __html: DOMPurify.sanitize(bannerTemplate.attributes.message_content),
        }}
      />
    </Alert>
  );
};

export default DowntimeBanner;
